import { Button } from "@ascleo/ui/components/ui/button";
import { Printer } from "lucide-react";
import type { AppointmentPrintControllerState } from "../use-appointment-print-controller";

/** Properties for the explicit patient-requested print action. @since 1.2.0 @example `<AppointmentPrintAction active={false} state={{ kind: "idle" }} onPrint={() => undefined} labels={labels} />` */
export interface AppointmentPrintActionProps {
  readonly active: boolean;
  readonly state: AppointmentPrintControllerState;
  readonly onPrint: () => void;
  readonly labels: {
    readonly action: string;
    readonly preparing: string;
    readonly error: string;
    readonly returned: string;
  };
}

/** Render the print command and truthful post-dialog status without owning booking state. @since 1.2.0 @example `<AppointmentPrintAction active={print.active} state={print.state} onPrint={() => void print.print()} labels={labels} />` */
export function AppointmentPrintAction({
  active,
  state,
  onPrint,
  labels,
}: AppointmentPrintActionProps) {
  return (
    <div className="grid gap-2">
      <Button
        data-testid="scheduler-done-print-button"
        size="2xl"
        variant="outline"
        disabled={active}
        onClick={onPrint}
      >
        <Printer aria-hidden className="size-5" />
        {active ? labels.preparing : labels.action}
      </Button>
      {state.kind === "failed" ? (
        <p
          role="alert"
          data-testid="scheduler-done-print-error"
          className="m-0 text-center text-base text-destructive-ink"
        >
          {labels.error}
        </p>
      ) : null}
      {state.kind === "system-completed" || state.kind === "unknown" ? (
        <p
          data-testid="scheduler-done-print-returned"
          className="m-0 text-center text-base text-muted-foreground"
        >
          {labels.returned}
        </p>
      ) : null}
    </div>
  );
}
